import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiImage, FiSave, FiX, FiMapPin } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const MAX_IMAGES = 8;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const buildImageUrl = (img) => {
  if (!img) return '';
  if (typeof img !== 'string') return '';
  if (/^https?:\/\//i.test(img)) return img;
  const base = API.defaults?.baseURL || (process.env.REACT_APP_API_URL || '');
  let s = String(img).trim();
  if (s.startsWith('/uploads/')) s = s.replace(/^\/uploads\//, '/api/uploads/');
  if (s.startsWith('uploads/')) s = s.replace(/^uploads\//, '/api/uploads/');
  if (!s.startsWith('/api/uploads') && !s.startsWith('/')) s = `/api/uploads/${s.replace(/^\/+/, '')}`;
  try { return new URL(s, base || window.location.origin).toString(); } catch { return s; }
};

// ✅ small helper (so you don't need a separate cityAPI file)
const fetchCitiesWithFallback = async () => {
  // try multiple endpoints to match your backend variations
  const tries = [
    { url: '/cities', params: { active: true } },
    { url: '/admin/cities', params: { active: true } },
    { url: '/cities/active', params: {} },
  ];

  let lastErr = null;

  for (const t of tries) {
    try {
      const res = await API.get(t.url, { params: t.params });
      const data = res?.data;
      const list = data?.data || data?.cities || data || [];
      if (Array.isArray(list)) return list;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error('Failed to load cities');
};

const CreateDeal = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const { user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [cities, setCities] = useState([]); // ✅ ADDED
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalPrice: '',
    price: '',
    category: '',
    validFrom: '',
    validUntil: '',
    quantity: '',
    // ✅ location removed from UI, but we keep compatibility in payload using selected city name
    city: '',            // ✅ ADDED
    terms: '',
    highlights: [''],
    images: [], // File objects
    merchantId: ''
  });

  const [errors, setErrors] = useState({});
  const imagePreviewsRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    fetchCategories();
    fetchMerchantsIfAdmin();
    loadCities(); // ✅ ADDED

    return () => {
      mountedRef.current = false;
      imagePreviewsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
      imagePreviewsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCities = async () => {
    try {
      const list = await fetchCitiesWithFallback();
      if (!mountedRef.current) return;
      // optional: keep only active if backend returns mixed
      const filtered = list.filter(c => c?.active !== false);
      setCities(filtered);
    } catch (error) {
      console.error('Error loading cities:', error);
      if (!mountedRef.current) return;
      setCities([]);
      // optional toast (you can remove if noisy)
      // toast.warn("Impossible de charger les villes");
    }
  };

  const isAdmin = useMemo(() => {
    if (!user) return false;
    if (typeof user.role === 'string') return user.role.toLowerCase().includes('admin');
    if (Array.isArray(user.roles)) return user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'));
    if (user.role && typeof user.role.name === 'string') return user.role.name.toLowerCase().includes('admin');
    return false;
  }, [user]);

  const fetchMerchantsIfAdmin = async () => {
    try {
      const endpoints = ['/users/me', '/auth/me', '/me'];
      let me = null;
      for (const ep of endpoints) {
        try {
          const res = await API.get(ep);
          me = res?.data?.data ?? res?.data ?? res;
          if (me) break;
        } catch (e) {}
      }
      if (!me) return;

      const roleStr = ((me.role && (typeof me.role === 'string' ? me.role : me.role.name)) || (Array.isArray(me.roles) && me.roles.join(',')) || '').toLowerCase();
      if (roleStr.includes('admin')) {
        try {
          const { data } = await API.get('/admin/merchants');
          if (!mountedRef.current) return;
          setMerchants(data?.data || data || []);
        } catch (err) {
          try {
            const { data } = await API.get('/merchants');
            if (!mountedRef.current) return;
            setMerchants(data?.data || data || []);
          } catch (e) { console.warn('no merchants', e); }
        }
      }
    } catch (e) { console.warn(e); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await API.get('/categories');
      if (!mountedRef.current) return;
      setCategories(data?.data || data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement catégories');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleHighlightChange = (i, v) => {
    const arr = [...formData.highlights];
    arr[i] = v;
    setFormData(prev => ({ ...prev, highlights: arr }));
  };

  const addHighlight = () => setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i) => setFormData(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existing = formData.images || [];
    const spaceLeft = Math.max(0, MAX_IMAGES - existing.length);

    const allowed = files.slice(0, spaceLeft).filter(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.warn(`${f.name} ignoré (trop gros)`);
        return false;
      }
      return true;
    });

    const previews = allowed.map(f => URL.createObjectURL(f));
    imagePreviewsRef.current = [...imagePreviewsRef.current, ...previews];

    setFormData(prev => ({ ...prev, images: [...existing, ...allowed] }));
    e.target.value = null;
  };

  const removeImageAt = (index) => {
    const urls = imagePreviewsRef.current;
    const url = urls[index];
    if (url) { try { URL.revokeObjectURL(url); } catch (e) {} }
    imagePreviewsRef.current = urls.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const calculateDiscount = () => {
    const orig = parseFloat(formData.originalPrice) || 0;
    const cur = parseFloat(formData.price) || 0;
    if (orig > 0 && cur > 0 && cur < orig) return Math.round(((orig - cur) / orig) * 100);
    return 0;
  };

  const validate = () => {
    const e = {};
    if (!formData.title?.trim()) e.title = 'Titre requis';
    if (!formData.description?.trim()) e.description = 'Description requise';

    const orig = Number(formData.originalPrice);
    const price = Number(formData.price);

    if (!Number.isFinite(orig) || orig <= 0) e.originalPrice = 'Prix original invalide';
    if (!Number.isFinite(price) || price <= 0) e.price = 'Prix du deal invalide';
    if (Number.isFinite(orig) && Number.isFinite(price) && price >= orig) e.price = 'Prix du deal doit être plus petit que prix original';

    if (!formData.category) e.category = 'Catégorie requise';

    // ✅ city required (instead of location)
    if (!formData.city) e.city = 'Ville requise';

    if (!formData.quantity || parseInt(formData.quantity, 10) < 1) e.quantity = 'Quantité invalide';
    if (!formData.validFrom) e.validFrom = 'Date début requise';
    if (!formData.validUntil) e.validUntil = 'Date fin requise';
    if (formData.validFrom && formData.validUntil) {
      if (new Date(formData.validFrom) > new Date(formData.validUntil)) e.validUntil = 'Date fin doit être après début';
    }

    if ((formData.images || []).length === 0) e.images = 'Au moins une image requise';
    if ((formData.images || []).length > MAX_IMAGES) e.images = `Max ${MAX_IMAGES} images`;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Corrige les erreurs'); return; }
    setLoading(true);
    setUploadProgress(null);

    try {
      const payload = new FormData();
      payload.append('title', formData.title.trim());
      payload.append('description', formData.description.trim());
      payload.append('originalPrice', String(Number(formData.originalPrice || 0)));
      payload.append('discountedPrice', String(Number(formData.price || 0)));
      payload.append('category', formData.category);
      payload.append('quantity', String(Math.max(0, parseInt(formData.quantity || '0', 10))));

      // ✅ send city id
      payload.append('city', formData.city);

      // ✅ backwards compatibility: also send location as city name
      const selectedCity = (cities || []).find(c => String(c?._id) === String(formData.city));
      payload.append('location', selectedCity?.name || '');

      if (formData.validFrom) payload.append('startDate', new Date(formData.validFrom).toISOString());
      if (formData.validUntil) payload.append('endDate', new Date(formData.validUntil).toISOString());
      payload.append('terms', formData.terms || '');

      const highlights = (formData.highlights || []).map(h => h.trim()).filter(Boolean);
      if (highlights.length) payload.append('highlights', JSON.stringify(highlights));

      (formData.images || []).forEach(file => payload.append('images', file));

      const config = {
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            if (mountedRef.current) setUploadProgress(pct);
          }
        }
      };

      let endpoint = '/deals';
      if (isAdmin) {
        if (formData.merchantId) endpoint = `/admin/merchants/${formData.merchantId}/deals`;
        else endpoint = '/admin/deals';
      } else {
        endpoint = '/merchant/deals';
      }

      let res;
      try {
        res = await API.post(endpoint, payload, config);
      } catch (err) {
        if (endpoint !== '/deals') res = await API.post('/deals', payload, config);
        else throw err;
      }

      const data = res?.data ?? res;
      if (data?.success) {
        toast.success(data.message || 'Deal créé');
        if (!mountedRef.current) return;
        navigate(data.redirect || (isAdmin ? '/admin/deals' : '/merchant/deals'));
      } else {
        toast.error(data?.message || 'Erreur création');
        if (data?.errors) setErrors(prev => ({ ...prev, ...data.errors }));
      }
    } catch (err) {
      console.error('create error', err);
      const msg = err?.response?.data?.message || err?.message || 'Erreur';
      const srvErrs = err?.response?.data?.errors || {};
      setErrors(prev => ({ ...prev, ...srvErrs }));
      toast.error(msg);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setUploadProgress(null);
      }
    }
  };

  const discount = calculateDiscount();

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Créer un Nouveau Deal</h1>
          <p className="text-gray-600 mt-1">Remplissez les informations de votre offre</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Informations Principales</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg ${errors.title ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg ${errors.category ? 'border-red-300' : 'border-gray-300'}`}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                {/* ✅ REPLACED location by city select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      value={formData.city}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, city: e.target.value }));
                        setErrors(prev => ({ ...prev, city: undefined }));
                      }}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                        errors.city ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Sélectionnez une ville</option>
                      {cities.map((city) => (
                        <option key={city._id} value={city._id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
                </div>
              </div>

              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attribuer au Marchand (optionnel)
                  </label>
                  <select
                    name="merchantId"
                    value={formData.merchantId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">Aucun</option>
                    {merchants.map(m => (
                      <option key={m._id || m.id} value={m._id || m.id}>
                        {m.businessName || m.email || m._id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Prix et Remise</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  placeholder="Prix original"
                  className={`px-4 py-2 border rounded-lg ${errors.originalPrice ? 'border-red-300' : 'border-gray-300'}`}
                />
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Prix deal"
                  className={`px-4 py-2 border rounded-lg ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                />
                <div className="flex items-center h-11 px-4 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-lg font-bold text-green-600">-{discount}%</span>
                </div>
              </div>

              <div>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Quantité"
                  className={`w-full px-4 py-2 border rounded-lg ${errors.quantity ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Période de Validité</h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="date"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleChange}
                className={`px-4 py-2 border rounded-lg ${errors.validFrom ? 'border-red-300' : 'border-gray-300'}`}
              />
              <input
                type="date"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleChange}
                className={`px-4 py-2 border rounded-lg ${errors.validUntil ? 'border-red-300' : 'border-gray-300'}`}
              />
            </div>
          </div>

          {/* Highlights */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b flex justify-between">
              <h2 className="text-xl font-semibold">Points Forts</h2>
              <button
                type="button"
                onClick={addHighlight}
                className="px-3 py-1 bg-primary-600 text-white rounded"
              >
                Ajouter
              </button>
            </div>
            <div className="p-6 space-y-3">
              {formData.highlights.map((h, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input
                    value={h}
                    onChange={(e) => handleHighlightChange(i, e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-lg"
                  />
                  {formData.highlights.length > 1 && (
                    <button type="button" onClick={() => removeHighlight(i)} className="p-2 text-red-600">
                      <FiX />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Images</h2></div>
            <div className="p-6">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-4">Glissez ou choisissez (max {MAX_IMAGES})</p>
                <input id="image-upload" type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                <label htmlFor="image-upload" className="inline-block px-4 py-2 bg-gray-100 rounded cursor-pointer">Choisir des images</label>
                {errors.images && <p className="mt-2 text-xs text-red-600">{errors.images}</p>}

                {(formData.images || []).length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {(formData.images || []).map((file, idx) => {
                      let url = imagePreviewsRef.current[idx];
                      if (!url && file) {
                        try { url = URL.createObjectURL(file); imagePreviewsRef.current[idx] = url; } catch (e) { url = ''; }
                      }
                      return (
                        <div key={idx} className="relative border rounded overflow-hidden">
                          {url
                            ? <img src={url} alt={`preview-${idx}`} className="w-full h-32 object-cover" />
                            : <div className="w-full h-32 bg-gray-100 flex items-center justify-center">No image</div>}
                          <button type="button" onClick={() => removeImageAt(idx)} className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-600">
                            <FiX />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Conditions</h2></div>
            <div className="p-6">
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(isAdmin ? '/admin/deals' : '/merchant/deals')}
              className="px-6 py-3 border rounded"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-3 bg-primary-600 text-white rounded"
            >
              {loading
                ? (uploadProgress !== null ? `Envoi ${uploadProgress}%` : 'Envoi...')
                : (<><FiSave className="mr-2" />Créer</>)
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDeal;
